/**
    * This class runs animations with stagger property for elements inside the modules.
    * All the elements included in the constructor's first parameter will have a staggered animation.
 */
class Stagger {
    /**
        * Fade In  constructor
        * 
        * There are some default parameters, so anyone can use the animation straight away.
        * 
        * @param {array/string} elements,
        * This parameter is REQUIRED to aim the element/s that we want to animate.
        * Can be a string with a css class. EG: '.my-element' or an array of HTML elements.
        * 
        * //////////////////////////////////////////////////////////////////////////////////////////
        * 
        * The rest of parameters are part of an Object. As such, you can customize the ones you want
        * and leave the rest with the default values if you choose.
        * If you want to use only default values, leave the method as: Stagger(element, {})
        * IMPORTANT: Remember to put these values between {} 
        * and to write them as an Object. 
        * Example: Stagger(element, {x: 100, duration: 1})
        * 
        * //////////////////////////////////////////////////////////////////////////////////////////
        * 
        * @param {number} x
        * This measures the amount of pixels that the element is going to move in the X axis.
        * Only accepts a Number. It represents Pixels
        * For moving to the LEFT, use a negative number. EG. {x: -100} 
        * For moving to the RIGHT, use a positive number. EG. {x: 100}
        * 
        * @param {number} y
        * This measures the amount of pixels that the element is going to move in the Y axis.
        * Only accepts a Number. It represents Pixels
        * For moving UP, use a negative number. EG. {y: -100} 
        * For moving DOWN, use a positive number. EG. {y: 100} 
        * 
        * 
        * @param {number} duration
        * This sets the duration of the animation, from start to end.
        * Only accepts a number (integer / float). It represent seconds
        * EG: {duration: 1} (1 sec) || {duration: .5} (half a sec)
        * 
        * @param {number} delay
        * This sets the time between the method initialization and the animation start.
        * Only accepts a number (integer / float). It represent seconds.
        * EG: {delay: 1} (1 sec) || {delay: .5} (half a sec)
        * 
        * @param {number/object} stagger
        * This sets the time between each element's animation start.
        * Accepts a number (integer / float) representing seconds, or an object with more specific properties
        * Check stagger properties here: https://greensock.com/docs/v3/Staggers
        * EG: {stagger: .5} (half a sec) ||  {stagger: {amount: 5, each: .5}}
        * 
        * @param {string} easing
        * This sets the ease of the animation.
        * Accepts only a string with one of these options:
        * "Power0" || "Power1" || "Power2" || "Power" || "Power4"
        * Check easings documentation here: https://greensock.com/docs/v3/Eases
        * 
    */
    constructor(elements = '.js-element-fade-in-up', {   
        x = 0, 
        y = 50, 
        duration = .5, 
        delay = .1, 
        stagger = .5, 
        easing = "Power0"
    }) {
        this.elements = elements,
        this.x = x
        this.y = y
        this.duration = duration,
        this.delay = delay,
        this.stagger = stagger,
        this.easing = easing
    }
    
    /**
        * Fade In w/Stagger animation
        * 
    */
    fadeIn() {
        if (this.elements) {
            const staggerItems = gsap.utils.toArray(this.elements);

            // set opacity to 0 for all elements (avoids showing it before animation)
            staggerItems.forEach(item => {
                item.style.opacity = 0;
            })

            // Animation for elements on page load
            gsap.set(staggerItems, { 
                y: this.y, 
                x: this.x,
                opacity: 0,
            });
            gsap.to(staggerItems, {
                x: 0,
                y: 0,
                opacity: 1,
                duration: this.duration,
                delay: this.delay,
                stagger: this.stagger,
                ease: `${this.easing}.easeIn`,
            });
        }
    }
    
    /**
    * This method runs the animation responding to scroll behavior
    * 
    * @param {boolean} once
    * Sets the animation repetition by entering or leaving the viewport
    * If set to TRUE, the animation only will be run when module enters the VP
    * If set to FALSE, the animation only will be reset on leave and rerun on enter back
    * 
    */
    staggerOnScroll(once = true) {
        if (this.elements) {
            const itemsToScroll = gsap.utils.toArray(this.elements);

            // set opacity to 0 for all elements (avoids showing it before animation)
            itemsToScroll.forEach(item => {
                item.style.opacity = 0;
            })

            // Animation callbacks for triggering on scroll
            // for Enter
            const animationEnter = (batch) => gsap.fromTo(batch, {
                y: this.y, 
                x: this.x,
                opacity: 0,
            }, {
                x: 0,
                y: 0,
                opacity: 1,
                duration: this.duration,
                delay: this.delay,
                stagger: this.stagger,
                ease: `${this.easing}.easeIn`,
            })

            // for Leave
            const animationLeave = (batch) => gsap.to(batch, {
                x: 0,
                y: 0,
                opacity: 0,
                duration: this.duration,
                delay: this.delay,
                stagger: this.stagger,
                ease: `${this.easing}.easeIn`,
            })

            // Create batch of elements and apply scrollTrigger and animation callbacks 
            ScrollTrigger.batch(itemsToScroll, {
                start: "0 100%",
                end: "100% 10%",
                // markers: true,
                onEnter: (batch) => animationEnter(batch),
                onLeave: (batch) => once ? '' : animationLeave(batch),
                onLeaveBack: (batch) => once ? '' : animationLeave(batch),
                onEnterBack: (batch) => once ? '' : animationEnter(batch),
            });
        }
    }

    /**
     * A method for stopping the animation if needed
     */ 
    stop() {
        if (this.elements) {
            gsap.killTweensOf(this.elements);
        }
    }
}

export default Stagger;